/**
 * Cart page enhancement.
 *
 * cs-cart.liquid renders every quantity change as a plain form posting
 * updates[<line key>] to /cart/update, so the cart is fully usable with no
 * JavaScript — that is the fallback, not an afterthought. This file intercepts
 * those same submits and runs them through the Ajax cart instead, then swaps
 * the section's HTML in place, so the page does not flash on every +/-.
 *
 * Deliberately NOT reusing Horizon's cart-items-component: its quantity
 * selectors want its own markup and refs, and the whole point of the port is
 * that this is our markup.
 *
 * Note the ordering lesson from the product form — nothing here acts on an
 * optimistic signal. The section is only re-rendered once /cart/change.js has
 * answered, and the request is never raced by a navigation.
 */
(function () {
  let root = document.querySelector('[data-cs-cart]');
  if (!root) return;

  const sectionId = root.dataset.sectionId;
  if (!sectionId) return;

  /** Pull the single updates[key] field out of one of the line forms. */
  const readUpdate = (form) => {
    for (const [name, value] of new FormData(form).entries()) {
      const match = name.match(/^updates\[(.+)\]$/);
      if (match) return { key: match[1], quantity: Number(value) };
    }
    return null;
  };

  /**
   * Re-render this section from the server rather than patching the DOM by
   * hand: line prices, discounts, the total and the empty state all have to
   * move together, and the server already knows how to draw them.
   */
  const rerender = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Section render failed: ' + response.status);

    const html = await response.text();
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cs-cart]');
    if (!next) throw new Error('Section markup missing [data-cs-cart]');

    root.replaceWith(next);
    root = next;
    bind(next);
    updateBar();
  };

  /** Keep the header count honest — /cart/change.js dispatches no event. */
  const paintHeaderCount = (count) => {
    if (typeof window.csPaintCartCount === 'function') window.csPaintCartCount(count);
  };

  let inFlight = false;

  const submit = async (form) => {
    const update = readUpdate(form);
    if (!update || inFlight) return;

    inFlight = true;
    const container = form.closest('[data-cs-cart]') || document.querySelector('[data-cs-cart]');
    container?.classList.add('opacity-60', 'pointer-events-none');

    try {
      const response = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: update.key, quantity: update.quantity }),
      });

      if (!response.ok) throw new Error('Cart change failed: ' + response.status);

      const cart = await response.json();
      paintHeaderCount(cart.item_count);
      await rerender();
    } catch (error) {
      // Fall back to the plain form post rather than leaving the customer
      // staring at a stepper that silently does nothing.
      console.warn('[cs-cart] falling back to a form post:', error);
      form.submit();
      return;
    } finally {
      inFlight = false;
      document.querySelector('[data-cs-cart]')?.classList.remove('opacity-60', 'pointer-events-none');
    }
  };

  function bind(scope) {
    scope.querySelectorAll('[data-cs-cart-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submit(form);
      });
    });

    // Typing a quantity should commit on blur/enter without hunting for a
    // button; the sr-only submit still covers the no-JS case.
    scope.querySelectorAll('[data-cs-cart-qty]').forEach((input) => {
      input.addEventListener('change', () => {
        const form = input.closest('[data-cs-cart-form]');
        if (form) submit(form);
      });
    });
  }

  /**
   * Reveal the sticky checkout bar whenever the real Check out button is off
   * screen — above it after scrolling past, or below it on mobile, where the
   * summary sits under the whole list. Same rAF-throttled pattern as
   * cs-product.js, and the same reason the listener goes on .page-wrapper:
   * Horizon scrolls that element, not the window.
   *
   * Elements are re-queried on every tick rather than captured, because the
   * bar lives inside the section this file re-renders. The listeners are bound
   * once, here, so a re-render cannot stack up duplicates.
   */
  let raf = 0;
  let shown = null;

  const updateBar = () => {
    raf = 0;
    const bar = document.querySelector('[data-cs-cart-bar]');
    const anchor = document.querySelector('#cs-cart-summary button[name="checkout"]');
    if (!bar || !anchor) return;

    document.documentElement.style.setProperty('--cs-sticky-h', bar.offsetHeight + 'px');
    document.documentElement.classList.add('cs-sticky-page');

    const rect = anchor.getBoundingClientRect();
    const show = rect.bottom < 0 || rect.top > (window.innerHeight || 0);

    // A re-render hands back a bar in its default hidden state, so re-apply
    // even when `show` has not changed.
    bar.classList.toggle('translate-y-full', !show);
    bar.classList.toggle('translate-y-0', show);
    bar.setAttribute('aria-hidden', String(!show));
    bar.querySelectorAll('button').forEach((button) => {
      button.tabIndex = show ? 0 : -1;
    });
    shown = show;
  };

  const onScroll = () => {
    if (!raf) raf = requestAnimationFrame(updateBar);
  };

  document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  bind(root);
  updateBar();
})();
